import React from 'react';

// In-app documentation + a side-by-side feature comparison of chadha.surf's
// color & photo editing against DaVinci Resolve (video) and Adobe Lightroom
// (photo). Kept in sync with the grade engine in utils/grade.js + api/grade.js.

const ST = {
  par: { label: 'On par', cls: 'is-par' },
  partial: { label: 'Partial', cls: 'is-partial' },
  win: { label: 'We win', cls: 'is-win' },
  missing: { label: 'Planned', cls: 'is-missing' },
};

// status: 'par' | 'partial' | 'win' | 'missing'
const VIDEO_ROWS = [
  ['Input color management (RED Log3G10/Wide-Gamut → 709, log decodes)', 'par', 'Full IPP2-style decode before any creative move.'],
  ['Primary correction (exposure, contrast, WB, blacks/whites)', 'par', 'Same controls, live WebGL == ffmpeg bake.'],
  ['Tone curve + per-channel (R/G/B) curves', 'par', 'Master + per-channel spline curves.'],
  ['HSL / colour mixer (8 bands)', 'par', 'Hue/sat/lum per band with skin protect.'],
  ['Colour grading wheels (shadow/mid/highlight/global)', 'par', 'Lift/gamma/gain-style 3-way + blending.'],
  ['Secondary qualifier curves (Hue→Hue/Sat/Luma, Sat→Sat, Luma→Sat)', 'par', '“Colour warper” secondaries.'],
  ['Scopes (histogram, waveform, RGB parade, vectorscope)', 'par', 'Live GPU read-back, skin-tone line.'],
  ['Local masks — radial + linear gradient + luminance keyer', 'par', 'Power-window equivalent with feather + luma range.'],
  ['AI masks — subject / sky', 'partial', 'Grok-Vision subject box + luminance-gated sky gradient (Resolve Magic Mask is per-pixel ML).'],
  ['Film effects — grain, halation, glow, vignette, clarity, sharpen', 'par', 'Filmic highlight bloom on spray/sun.'],
  ['Per-clip 3D LUT + movie-wide grade compositing', 'par', 'Each clip bakes its own LUT, global look on top.'],
  ['LUT import (.cube creative looks)', 'par', 'Import any 3D LUT — applied as a second LUT stage, preview == ffmpeg bake.'],
  ['Natural-language AI colour assistant (sees the frames)', 'win', 'Grok Vision drives sliders, presets AND masks. No Resolve equivalent.'],
  ['Noise reduction (spatial + temporal)', 'par', 'Edge-preserving bilateral preview · hqdn3d (spatial+temporal) on export.'],
  ['Grade keyframing over time', 'missing', 'Animate the grade across a clip — on the roadmap.'],
  ['Tracker-linked masks (mask follows the surfer)', 'missing', 'Reuse the subject path to drive a mask — roadmap.'],
];

const PHOTO_ROWS = [
  ['RAW develop (LibRaw, 16-bit linear proxy)', 'par', 'Full latitude, originals preserved.'],
  ['Basic panel (exposure/contrast/highlights/shadows/whites/blacks)', 'par', 'Same tonal controls.'],
  ['Tone curve + per-channel curves', 'par', 'Master + R/G/B splines.'],
  ['HSL / colour mixer', 'par', '8-band hue/sat/lum.'],
  ['Colour grading (3-way + global)', 'par', 'Shadows/mids/highlights/global wheels.'],
  ['Detail — clarity, texture, dehaze, sharpening', 'par', 'Local-contrast + presence.'],
  ['Masking — radial + linear gradient', 'par', 'Lightroom radial + graduated filter equivalent.'],
  ['Range mask — luminance', 'par', 'Per-mask luminance keyer (feathered band).'],
  ['AI masks — subject / sky', 'partial', 'Grok-Vision “Select Subject” + luminance-gated sky. (Lightroom uses on-device ML masks.)'],
  ['Presets (save/apply, per-look amount)', 'par', 'Named presets + intensity dial.'],
  ['LUT import (.cube)', 'par', 'Drop in a creative 3D LUT; copy/paste a look between photos.'],
  ['Noise reduction', 'par', 'Edge-preserving denoise — bilateral preview, hqdn3d on export.'],
  ['Copy / paste develop settings', 'par', 'Carry a whole grade (incl. masks) to another photo.'],
  ['Export — JPEG, 16-bit TIFF, PNG, WebP + flat/neutral handoff', 'par', 'Lightroom-grade delivery + log/neutral TIFF for round-trip.'],
  ['Crop + aspect lock', 'par', 'Non-destructive crop, ratio locks.'],
  ['Straighten + flip H/V', 'par', 'Free-angle straighten and flips — preview == export. 90° orient + lens correction planned.'],
  ['Natural-language AI assistant', 'win', 'Grok Vision tweaks the develop for you — beyond Lightroom’s point AI.'],
  ['Range mask — colour', 'missing', 'Key by colour as well as luminance — roadmap.'],
  ['Brush / freehand mask', 'missing', 'Paint a mask — roadmap.'],
  ['Healing / spot removal', 'missing', 'Clone/heal blemishes — roadmap.'],
  ['Straighten / rotate + lens corrections', 'missing', 'Geometry + distortion/vignette correction — roadmap.'],
  ['Batch sync / copy-paste settings', 'missing', 'Sync one develop across many photos — roadmap.'],
];

function CmpTable({ title, them, rows }) {
  return (
    <div className="cs-cmp-block">
      <div className="cs-cmp-tabletitle">
        <h3>{title}</h3>
        <span className="cs-cmp-vs">chadha.surf vs {them}</span>
      </div>
      <div className="cs-cmp-table">
        {rows.map(([feat, status, note]) => {
          const s = ST[status] || ST.partial;
          return (
            <div key={feat} className="cs-cmp-row">
              <span className={`cs-cmp-badge ${s.cls}`}>{s.label}</span>
              <div className="cs-cmp-cell">
                <span className="cs-cmp-feat">{feat}</span>
                <span className="cs-cmp-note">{note}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ColorComparisonDoc() {
  const count = (rows, st) => rows.filter((r) => r[1] === st).length;
  const vAll = VIDEO_ROWS.length;
  const pAll = PHOTO_ROWS.length;
  return (
    <div className="cs-cmp">
      <p className="cs-section-sub">
        chadha.surf carries a complete, non-destructive colour pipeline shared by clips and RAW
        photos: a real-time WebGL2 preview that bakes <b>identically</b> in ffmpeg (preview == export),
        proper RED colour management, broadcast scopes, secondary qualifier curves, local masks, and a
        Grok-Vision assistant that can see your frames and grade for you. Here&apos;s where it lands against
        the tools it&apos;s measured against.
      </p>

      <div className="cs-cmp-stats">
        <div className="cs-cmp-stat">
          <div className="cs-cmp-statn">{count(VIDEO_ROWS, 'par') + count(VIDEO_ROWS, 'win')}/{vAll}</div>
          <div className="cs-cmp-statl">Video features on par or ahead of Resolve</div>
        </div>
        <div className="cs-cmp-stat">
          <div className="cs-cmp-statn">{count(PHOTO_ROWS, 'par') + count(PHOTO_ROWS, 'win')}/{pAll}</div>
          <div className="cs-cmp-statl">Photo features on par or ahead of Lightroom</div>
        </div>
        <div className="cs-cmp-stat">
          <div className="cs-cmp-statn">2×</div>
          <div className="cs-cmp-statl">Capabilities neither tool has (AI grade + mask assistant)</div>
        </div>
      </div>

      <div className="cs-cmp-verdict">
        <b>Verdict.</b> For the surf-film workflow this app is built for — RED RAW in, graded clips
        and magazine stills out — the core grading is at parity with Resolve/Lightroom and ahead on AI
        assistance. The remaining gaps are <i>specialist</i> tools (grade keyframing, tracker-linked &amp;
        brush masks, healing, lens correction, NR, batch sync) which are scoped on the roadmap below.
      </div>

      <CmpTable title="Video grading" them="DaVinci Resolve" rows={VIDEO_ROWS} />
      <CmpTable title="Photo developing" them="Adobe Lightroom" rows={PHOTO_ROWS} />

      <div className="cs-cmp-block">
        <div className="cs-cmp-tabletitle"><h3>Where the Grok AI assistant plugs in</h3></div>
        <div className="cs-cmp-table">
          {[
            ['Global colour & light', 'Reads the original vs graded frame and sets any slider (exposure, WB, curves, HSL, wheels) to absolute target values.'],
            ['Looks / presets', 'Picks a preset and a strength (0–100%), then layers your fine-tunes on top.'],
            ['Local masks', 'Adds radial / gradient masks with a luminance keyer and local tweaks (“darken the sky”, “warm the surfer”).'],
            ['AI subject mask', 'Locates the main subject with Grok Vision and centres a radial mask on it.'],
            ['What stays manual', 'Pixel-level work — healing, brush strokes, frame-accurate keyframing — is human-driven; the assistant proposes, you accept (with Undo).'],
          ].map(([k, v]) => (
            <div key={k} className="cs-cmp-row">
              <span className="cs-cmp-badge is-win">Grok</span>
              <div className="cs-cmp-cell">
                <span className="cs-cmp-feat">{k}</span>
                <span className="cs-cmp-note">{v}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="cs-cmp-block">
        <div className="cs-cmp-tabletitle"><h3>Roadmap — closing the remaining gaps</h3></div>
        <div className="cs-cmp-table">
          {[
            ['Tracker-linked masks', 'Drive a mask centre from the existing per-clip subject path so a local grade follows the surfer.'],
            ['Grade keyframing', 'Time-vary the whole grade across a clip; bake via ffmpeg sendcmd / segmenting.'],
            ['Healing / spot removal', 'Store clone/heal regions; sample a source offset in GL and a matching ffmpeg geq region on bake.'],
            ['90° orient + lens correction', 'Quarter-turn orientation and barrel/pincushion correction (straighten + flip already shipped).'],
            ['Colour range masks + brush', 'Extend the keyer to chroma and add a freehand brush alpha.'],
          ].map(([k, v]) => (
            <div key={k} className="cs-cmp-row">
              <span className="cs-cmp-badge is-missing">Planned</span>
              <div className="cs-cmp-cell">
                <span className="cs-cmp-feat">{k}</span>
                <span className="cs-cmp-note">{v}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
